// ─── Modèles d'inventaire prêts à charger ─────────────────────────
// Catalogue de structures type (arbre de catégories + propriétés) qu'une
// famille peut charger d'un coup à la création d'un inventaire, au lieu de
// tout créer à la main. 100 % offline (aucune dépendance, aucun accès réseau).
//
// Un modèle est une DONNÉE PURE, sans identifiants : `instancierModele()` en
// produit un arbre/propriétés prêts pour Firestore, avec des `id` fraîchement
// générés. On peut ensuite tout éditer/compléter comme un inventaire normal.
//
//   modele : { id, nom, emoji, desc, arbre:[noeud], proprietes:[prop] }
//   noeud  : { nom, emoji, enfants:[noeud] }            (sans id ici)
//   prop   : { nom, type:'texte'|'nombre'|'date'|'liste', options?:[string] }

import { nouvelId } from '../services/inventaires';

// Fabrique lisible d'un nœud de catégorie : c('Nom', '👕', [sous-nœuds…]).
const c = (nom, emoji, enfants = []) => ({ nom, emoji, enfants });
// Fabrique d'une propriété : p('Taille', 'texte') ou p('État', 'liste', ['Neuf','Bon']).
const p = (nom, type, options) => (options ? { nom, type, options } : { nom, type });

export const MODELES_INVENTAIRE = [
  // ── Garde-robe / vêtements ──────────────────────────────────────
  {
    id: 'garde-robe', nom: 'Garde-robe', emoji: '👕',
    i18n: 'inventaire.tplGardeRobe', i18nDesc: 'inventaire.tplGardeRobeDesc',
    desc: 'Vêtements, chaussures et accessoires par type',
    arbre: [
      c('Hauts', '👕', [c('T-shirts', '👕'), c('Chemises', '👔'), c('Pulls & gilets', '🧥')]),
      c('Bas', '👖', [c('Pantalons', '👖'), c('Jupes & robes', '👗'), c('Shorts', '🩳')]),
      c('Sous-vêtements', '🩲', [c('Slips & culottes', '🩲'), c('Chaussettes', '🧦'), c('Pyjamas', '🛌')]),
      c('Chaussures', '👟', [c('Baskets', '👟'), c('Ville', '👞'), c('Bottes', '🥾')]),
      c('Accessoires', '🧢', [c('Bonnets & casquettes', '🧢'), c('Écharpes & gants', '🧣'), c('Ceintures', '➰')]),
      c('Saison', '🌦️', [c('Manteaux', '🧥'), c('Maillots de bain', '🩱')]),
    ],
    proprietes: [p('Taille', 'texte'), p('Marque', 'texte'), p("Date d'achat", 'date'),
      p('État', 'liste', ['Neuf', 'Bon', 'Usé', 'À donner'])],
  },

  // ── Placards & frigo (nourriture) ───────────────────────────────
  {
    id: 'placards-frigo', nom: 'Placards & frigo', emoji: '🍽️',
    i18n: 'inventaire.tplPlacardsFrigo', i18nDesc: 'inventaire.tplPlacardsFrigoDesc',
    desc: 'Épicerie, conserves, frais, surgelés, boissons',
    arbre: [
      c('Épicerie salée', '🧂', [c('Pâtes & riz', '🍝'), c('Farine & semoule', '🌾'), c('Huile & vinaigre', '🫒'), c('Épices & sauces', '🧂')]),
      c('Épicerie sucrée', '🍬', [c('Gâteaux & biscuits', '🍪'), c('Chocolat', '🍫'), c('Confiture & miel', '🍯'), c('Céréales', '🥣')]),
      c('Conserves', '🥫', [c('Légumes', '🥫'), c('Poisson', '🐟'), c('Plats préparés', '🍲')]),
      c('Frais', '🧀', [c('Produits laitiers', '🧀'), c('Œufs', '🥚'), c('Viande & charcuterie', '🥓'), c('Fruits & légumes', '🥦')]),
      c('Surgelés', '❄️', [c('Légumes', '🥦'), c('Plats', '🍕'), c('Glaces', '🍦')]),
      c('Boissons', '🥤', [c('Eau', '💧'), c('Sodas & jus', '🧃'), c('Café & thé', '☕'), c('Alcool', '🍷')]),
      c('Petit-déjeuner', '🥐', []),
    ],
    proprietes: [p('DLC / DLUO', 'date'), p('Marque', 'texte'), p('Ouvert ?', 'liste', ['Non', 'Oui'])],
  },

  // ── Ustensiles de cuisine ───────────────────────────────────────
  {
    id: 'ustensiles-cuisine', nom: 'Ustensiles de cuisine', emoji: '🍳',
    i18n: 'inventaire.tplUstensilesCuisine', i18nDesc: 'inventaire.tplUstensilesCuisineDesc',
    desc: 'Cuisson, vaisselle, couverts, électroménager',
    arbre: [
      c('Cuisson', '🍳', [c('Casseroles', '🥘'), c('Poêles', '🍳'), c('Plats au four', '🧑‍🍳'), c('Cocotte', '🍲')]),
      c('Vaisselle', '🍽️', [c('Assiettes', '🍽️'), c('Bols', '🥣'), c('Verres', '🥛'), c('Tasses & mugs', '☕')]),
      c('Couverts', '🍴', [c('Fourchettes', '🍴'), c('Couteaux', '🔪'), c('Cuillères', '🥄')]),
      c('Petit électroménager', '🔌', [c('Mixeur & blender', '🌀'), c('Grille-pain', '🍞'), c('Cafetière', '☕'), c('Robot', '🤖')]),
      c('Préparation', '🔪', [c('Saladiers', '🥗'), c('Planches', '🪵'), c('Balance', '⚖️'), c('Moules', '🧁')]),
      c('Rangement', '🥡', [c('Boîtes', '🥡'), c('Bocaux', '🫙')]),
    ],
    proprietes: [p('Matière', 'liste', ['Inox', 'Anti-adhésif', 'Fonte', 'Verre', 'Plastique', 'Bois']),
      p('Marque', 'texte')],
  },

  // ── Valise / vacances ───────────────────────────────────────────
  {
    id: 'valise-vacances', nom: 'Valise / Vacances', emoji: '🧳',
    i18n: 'inventaire.tplValiseVacances', i18nDesc: 'inventaire.tplValiseVacancesDesc',
    desc: 'Check-list à emporter en voyage',
    arbre: [
      c('Vêtements', '👕', [c('Hauts', '👕'), c('Bas', '👖'), c('Sous-vêtements', '🩲'), c('Chaussures', '👟'), c('Maillots', '🩱')]),
      c('Toilette', '🧴', [c('Brosse à dents', '🪥'), c('Savon & shampoing', '🧴'), c('Serviettes', '🧻')]),
      c('Papiers', '🪪', [c('Pièces d’identité', '🪪'), c('Billets & réservations', '🎫'), c('Assurance', '📄')]),
      c('Électronique', '🔌', [c('Chargeurs', '🔌'), c('Câbles', '🔗'), c('Adaptateur', '🔲'), c('Écouteurs', '🎧')]),
      c('Enfants', '🧸', [c('Doudou', '🧸'), c('Jeux', '🎲'), c('Poussette', '🍼')]),
      c('Pharmacie', '💊', [c('Médicaments', '💊'), c('Pansements', '🩹'), c('Crème solaire', '🧴')]),
    ],
    proprietes: [p('À emporter ?', 'liste', ['À faire', 'Dans la valise']), p('Pour qui', 'texte')],
  },

  // ── Outils & bricolage ──────────────────────────────────────────
  {
    id: 'outils-bricolage', nom: 'Outils & bricolage', emoji: '🛠️',
    i18n: 'inventaire.tplOutilsBricolage', i18nDesc: 'inventaire.tplOutilsBricolageDesc',
    desc: 'Outils, quincaillerie, peinture, jardin',
    arbre: [
      c('Outils à main', '🔧', [c('Tournevis', '🪛'), c('Marteaux', '🔨'), c('Clés', '🔧'), c('Pinces', '🗜️'), c('Mesure', '📏')]),
      c('Électroportatif', '🔌', [c('Perceuse', '🪫'), c('Visseuse', '🔩'), c('Scie', '🪚'), c('Ponceuse', '🌀')]),
      c('Quincaillerie', '🔩', [c('Vis', '🔩'), c('Clous', '📌'), c('Chevilles', '🧷'), c('Boulons', '⚙️')]),
      c('Peinture', '🎨', [c('Pots', '🪣'), c('Pinceaux & rouleaux', '🖌️'), c('Adhésifs', '🩹')]),
      c('Électricité', '💡', [c('Ampoules', '💡'), c('Câbles', '🔌'), c('Piles', '🔋')]),
      c('Jardin', '🌱', [c('Arrosage', '🚿'), c('Outils de jardin', '🪏'), c('Graines', '🌱')]),
    ],
    proprietes: [p('Marque', 'texte'), p('État', 'liste', ['Neuf', 'Bon', 'Usé', 'HS'])],
  },

  // ── Jouets & jeux ───────────────────────────────────────────────
  {
    id: 'jouets-jeux', nom: 'Jouets & jeux', emoji: '🧸',
    i18n: 'inventaire.tplJouetsJeux', i18nDesc: 'inventaire.tplJouetsJeuxDesc',
    desc: 'Jouets des enfants par type',
    arbre: [
      c('Jeux de société', '🎲', []),
      c('Construction', '🧱', [c('Briques', '🧱'), c('Circuits', '🛤️')]),
      c('Peluches & poupées', '🧸', []),
      c('Extérieur', '⚽', [c('Ballons', '⚽'), c('Vélos & trottinettes', '🛴')]),
      c('Créatif', '🎨', [c('Dessin', '✏️'), c('Pâte à modeler', '🟡')]),
      c('Jeux vidéo', '🎮', []),
      c('Livres & éveil', '📚', []),
    ],
    proprietes: [p('Âge conseillé', 'texte'), p('État', 'liste', ['Neuf', 'Bon', 'Abîmé', 'À donner']),
      p('Pièces complètes ?', 'liste', ['Oui', 'Non', 'À vérifier'])],
  },

  // ── Bibliothèque / livres ───────────────────────────────────────
  {
    id: 'bibliotheque', nom: 'Bibliothèque', emoji: '📚',
    i18n: 'inventaire.tplBibliotheque', i18nDesc: 'inventaire.tplBibliothequeDesc',
    desc: 'Livres par genre',
    arbre: [
      c('Romans', '📕', [c('Policier', '🕵️'), c('Science-fiction', '🚀'), c('Classiques', '🏛️')]),
      c('BD & mangas', '💥', []),
      c('Jeunesse', '🧒', [c('Albums', '🖼️'), c('Premières lectures', '📖')]),
      c('Documentaires', '📗', [c('Histoire', '🏺'), c('Sciences', '🔬'), c('Art', '🎨')]),
      c('Cuisine & loisirs', '🍳', []),
      c('Scolaire', '🎓', []),
    ],
    proprietes: [p('Auteur', 'texte'), p('Lu ?', 'liste', ['À lire', 'En cours', 'Lu']),
      p('Prêté à', 'texte')],
  },

  // ── Pharmacie / trousse de soins ────────────────────────────────
  {
    id: 'pharmacie', nom: 'Pharmacie', emoji: '💊',
    i18n: 'inventaire.tplPharmacie', i18nDesc: 'inventaire.tplPharmacieDesc',
    desc: 'Médicaments et matériel de soin',
    arbre: [
      c('Médicaments', '💊', [c('Douleur & fièvre', '🤒'), c('Rhume & gorge', '🤧'), c('Digestion', '🫃'), c('Allergie', '🤧')]),
      c('Premiers soins', '🩹', [c('Pansements', '🩹'), c('Désinfectant', '🧴'), c('Compresses & bandes', '🩹')]),
      c('Matériel', '🌡️', [c('Thermomètre', '🌡️'), c('Ciseaux & pince', '✂️')]),
      c('Bébé', '🍼', []),
    ],
    proprietes: [p('DLC', 'date'), p('Ordonnance ?', 'liste', ['Non', 'Oui']), p('Pour qui', 'texte')],
  },

  // ── Cave à vin ──────────────────────────────────────────────────
  {
    id: 'cave-vin', nom: 'Cave à vin', emoji: '🍷',
    i18n: 'inventaire.tplCaveVin', i18nDesc: 'inventaire.tplCaveVinDesc',
    desc: 'Bouteilles par couleur et région',
    arbre: [
      c('Rouge', '🍷', [c('Bordeaux', '🏰'), c('Bourgogne', '🍇'), c('Rhône', '🌄'), c('Autres', '🍷')]),
      c('Blanc', '🥂', [c('Sec', '🍾'), c('Moelleux', '🍯')]),
      c('Rosé', '🌸', []),
      c('Effervescent', '🍾', [c('Champagne', '🍾'), c('Crémant', '🥂')]),
    ],
    proprietes: [p('Millésime', 'nombre'), p('Région', 'texte'), p('À boire avant', 'nombre'),
      p('Occasion', 'liste', ['Quotidien', 'Repas', 'Garde', 'Cadeau'])],
  },

  // ── Salle de bain / hygiène ─────────────────────────────────────
  {
    id: 'salle-de-bain', nom: 'Salle de bain', emoji: '🧴',
    i18n: 'inventaire.tplSalleDeBain', i18nDesc: 'inventaire.tplSalleDeBainDesc',
    desc: 'Hygiène, beauté, entretien',
    arbre: [
      c('Hygiène', '🧼', [c('Savon & gel douche', '🧼'), c('Shampoing', '🧴'), c('Dents', '🪥'), c('Papier & coton', '🧻')]),
      c('Beauté', '💄', [c('Soins visage', '🧖'), c('Maquillage', '💄'), c('Parfums', '🌸')]),
      c('Rasage & épilation', '🪒', []),
      c('Bébé', '🍼', [c('Couches', '👶'), c('Liniment', '🧴')]),
      c('Entretien', '🧽', [c('Nettoyants', '🧽'), c('Serviettes & linge', '🧺')]),
    ],
    proprietes: [p('Marque', 'texte'), p('Ouvert ?', 'liste', ['Non', 'Oui']), p('DLC', 'date')],
  },
];

// ─── Instanciation ────────────────────────────────────────────────
// Transforme un modèle (données pures, sans id) en { arbre, proprietes }
// prêts à écrire dans Firestore, avec des identifiants fraîchement générés.
function instancierNoeud(n) {
  return {
    id:      nouvelId('cat'),
    nom:     n.nom || '',
    emoji:   n.emoji || '📁',
    enfants: (n.enfants || []).map(instancierNoeud),
  };
}

export function instancierModele(modeleOuId) {
  const m = typeof modeleOuId === 'string'
    ? MODELES_INVENTAIRE.find(x => x.id === modeleOuId)
    : modeleOuId;
  if (!m) return { arbre: [], proprietes: [], nom: '', emoji: '📦' };
  return {
    nom:        m.nom || '',
    emoji:      m.emoji || '📦',
    arbre:      (m.arbre || []).map(instancierNoeud),
    proprietes: (m.proprietes || []).map(pr => ({
      id:      nouvelId('prop'),
      nom:     pr.nom || '',
      type:    pr.type || 'texte',
      ...(pr.options ? { options: pr.options.slice() } : {}),
    })),
  };
}

// ─── Libellés traduisibles ────────────────────────────────────────
// Ce fichier reste PUR (pas de hook). Les libellés fr d'origine servent de
// fallback ; passe la fonction `t` (obtenue via useT() dans l'écran) pour
// obtenir la traduction. Rétro-compatible : sans `t`, on renvoie le fr.
//   labelTemplate(tpl)      → 'Garde-robe'
//   labelTemplate(tpl, t)   → t('inventaire.tplGardeRobe')
export function labelTemplate(tpl, t) {
  if (!tpl) return '';
  return (t && tpl.i18n) ? t(tpl.i18n) : (tpl.nom || '');
}
export function descTemplate(tpl, t) {
  if (!tpl) return '';
  return (t && tpl.i18nDesc) ? t(tpl.i18nDesc) : (tpl.desc || '');
}

// Nombre de catégories (tous niveaux) d'un modèle — pour l'affichage « N catégories ».
export function nbCategoriesModele(m) {
  let n = 0;
  (function compte(list) {
    for (const x of (list || [])) { n += 1; compte(x.enfants); }
  })(m && m.arbre);
  return n;
}
